import { authenticatedInstance } from '../index/index.service';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';

// Summary: Multiplexed SuperAdmin SSE channel that carries every admin-scope domain (tenants + users + payment terminals).
// Details: docs/function-library/web/services.md#admin-events-service

export type AdminTenantsEvent =
  | { type: 'tenant.upserted'; tenant: unknown }
  | { type: 'tenant.active-changed'; tenantId: string; isActive: boolean };

export type AdminUsersEvent =
  | { type: 'user.upserted'; user: unknown }
  | { type: 'user.deactivated'; userId: number };

export type AdminPaymentTerminalsEvent =
  | { type: 'payment-terminal.upserted'; terminal: unknown }
  | { type: 'payment-terminal.active-changed'; terminalId: number; isActive: boolean };

export type AdminApprovalRequestsEvent =
  | { type: 'approval-request.changed'; request: unknown };

export type AdminDashboardEvent =
  | { type: 'dashboard.changed'; tenantId: string; sourceType: string };

export type AdminDeploymentRefreshEvent =
  | { type: 'deployment.refresh-requested'; version: string };

export type AdminEvent =
  | AdminTenantsEvent
  | AdminUsersEvent
  | AdminPaymentTerminalsEvent
  | AdminApprovalRequestsEvent
  | AdminDashboardEvent
  | AdminDeploymentRefreshEvent;

const ADMIN_EVENT_TYPES: ReadonlyArray<AdminEvent['type']> = [
  'tenant.upserted',
  'tenant.active-changed',
  'user.upserted',
  'user.deactivated',
  'payment-terminal.upserted',
  'payment-terminal.active-changed',
  'approval-request.changed',
  'dashboard.changed',
  'deployment.refresh-requested',
];

// Summary: Mints a single-use SSE ticket for the merged admin stream.
// Details: docs/function-library/web/services.md#admin-events-service
async function mintAdminStreamTicket(): Promise<string> {
  const response = await authenticatedInstance.post<{ ticket: string; expiresAt: number }>(
    `/admin/stream-ticket`,
    {},
  );
  if (!response.data?.ticket) {
    throw new Error('Admin stream ticket missing in response');
  }
  return response.data.ticket;
}

// Summary: Opens the single multiplexed SuperAdmin SSE stream.
// Details: docs/function-library/web/services.md#admin-events-service
export function subscribeAdminEvents(
  onEvent: (event: AdminEvent) => void,
  onError?: () => void,
): () => void {
  return subscribeStream<AdminEvent>({
    mintTicket: () => mintAdminStreamTicket(),
    buildStreamUrl: (ticket, lastEventId) => defaultStreamUrl('/admin/stream', ticket, lastEventId),
    parseEvent: (payload) => {
      const candidate = payload as AdminEvent | undefined;
      if (
        candidate &&
        typeof candidate.type === 'string' &&
        (ADMIN_EVENT_TYPES as ReadonlyArray<string>).includes(candidate.type)
      ) {
        return candidate;
      }
      return null;
    },
    onEvent,
    onError,
  });
}


